import { c as _c } from "react-compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useResource, skipToken } from 'pulse/react';
import { Metrics } from 'framework-listing-lib/internal/metrics';
import listingLibPerformanceTracking, { MEASURE_MARK_NAMES } from 'framework-listing-lib/internal/utils/performanceTracking';
import { CrmSearchAllProperties, getPulseArgs, transformPulseResponseToCrmSearchResponse } from 'framework-listing-lib/internal/resources/pulseCrmSearch';
function getStatusCodeDimension(error) {
  const status = error === null || error === void 0 ? void 0 : error.status;
  return typeof status === 'number' ? String(status) : 'unknown';
}
function useTrackPulsePerf(t0) {
  "use memo";

  const $ = _c(7);
  const {
    enabled,
    hasData,
    trackPerformance: t1
  } = t0;
  const trackPerformance = t1 === undefined ? false : t1;
  const shouldTrack = enabled && trackPerformance;
  let t2;
  let t3;
  if ($[0] !== shouldTrack) {
    t2 = () => {
      if (shouldTrack) {
        listingLibPerformanceTracking.getPerformanceTracking(MEASURE_MARK_NAMES.CRM_SEARCH).markTimeStart();
      }
    };
    t3 = [shouldTrack];
    $[0] = shouldTrack;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== hasData || $[4] !== shouldTrack) {
    t4 = () => {
      if (shouldTrack && hasData) {
        listingLibPerformanceTracking.getPerformanceTracking(MEASURE_MARK_NAMES.CRM_SEARCH).markTimeEnd({
          fetch_path: "pulse"
        });
      }
    };
    t5 = [shouldTrack, hasData];
    $[3] = hasData;
    $[4] = shouldTrack;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
}
export default function usePulseCrmSearch(variables, options) {
  "use memo";

  const $ = _c(30);
  const {
    enabled,
    skip,
    pollInterval,
    fetchPolicy,
    trackPerformance
  } = options;
  const [localPollInterval, setLocalPollInterval] = useState(undefined);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = pollIntervalMs => setLocalPollInterval(pollIntervalMs);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const startPolling = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setLocalPollInterval(undefined);
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const stopPolling = t1;
  const isActive = enabled && !skip;
  let t2;
  if ($[2] !== isActive || $[3] !== variables) {
    t2 = isActive ? getPulseArgs(variables) : skipToken;
    $[2] = isActive;
    $[3] = variables;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = pollInterval !== null && pollInterval !== void 0 ? pollInterval : localPollInterval;
  const t4 = fetchPolicy === "cache-only";
  let t5;
  if ($[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = {
      params: t2,
      pollInterval: t3,
      passive: t4
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    data: pulseData,
    loading,
    error,
    stale
  } = useResource(CrmSearchAllProperties, t5);
  const objectTypeId = variables.fetchParams.objectTypeId;
  const isTracked = isActive && fetchPolicy !== "cache-only";
  let t6;
  let t7;
  if ($[9] !== isTracked || $[10] !== objectTypeId || $[11] !== pulseData) {
    t6 = () => {
      if (isTracked && pulseData) {
        Metrics.counter("crm-search-pulse-success", {
          object_type_id: objectTypeId
        }).increment();
      }
    };
    t7 = [isTracked, pulseData, objectTypeId];
    $[9] = isTracked;
    $[10] = objectTypeId;
    $[11] = pulseData;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[14] !== error || $[15] !== isTracked || $[16] !== objectTypeId) {
    t8 = () => {
      if (isTracked && error) {
        Metrics.counter("crm-search-pulse-failure", {
          object_type_id: objectTypeId,
          status_code: getStatusCodeDimension(error)
        }).increment();
      }
    };
    t9 = [isTracked, error, objectTypeId];
    $[14] = error;
    $[15] = isTracked;
    $[16] = objectTypeId;
    $[17] = t8;
    $[18] = t9;
  } else {
    t8 = $[17];
    t9 = $[18];
  }
  useEffect(t8, t9);
  let t10;
  if ($[19] !== pulseData) {
    t10 = transformPulseResponseToCrmSearchResponse(pulseData);
    $[19] = pulseData;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const data = t10;
  const t11 = pulseData !== undefined;
  let t12;
  if ($[21] !== isActive || $[22] !== t11 || $[23] !== trackPerformance) {
    t12 = {
      enabled: isActive,
      hasData: t11,
      trackPerformance
    };
    $[21] = isActive;
    $[22] = t11;
    $[23] = trackPerformance;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  useTrackPulsePerf(t12);
  const t13 = error !== null && error !== void 0 ? error : undefined;
  let t14;
  if ($[25] !== data || $[26] !== loading || $[27] !== stale || $[28] !== t13) {
    t14 = {
      data,
      loading,
      error: t13,
      stale,
      startPolling,
      stopPolling
    };
    $[25] = data;
    $[26] = loading;
    $[27] = stale;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  return t14;
}